import type { DirectoryOrganization } from "@repo/api/client";
import { Button, Flex, Icon, Select, Text, TextField } from "@repo/ui";
import { useState } from "react";
import { toast } from "sonner";
import { useApiClient } from "../../contexts/ApiContext";
import { US_STATES } from "../providers/components/ProviderNestedModal/usStates";
import {
	type DirectoryLocation,
	formatPhone,
	userFacingMessage,
} from "./directoryAdminUtils";

type LocationDraft = {
	/** React key; stable for unsaved locations, which have no id yet. */
	key: string;
	id?: string;
	address: string;
	city: string;
	state: string;
	zip: string;
	phone: string;
	fax: string;
};

function toDraft(location: DirectoryLocation): LocationDraft {
	return {
		key: location.id,
		id: location.id,
		address: location.address,
		city: location.city ?? "",
		state: location.state ?? "",
		zip: location.zip ?? "",
		phone: formatPhone(location.phone),
		fax: formatPhone(location.fax),
	};
}

function emptyLocation(): LocationDraft {
	return {
		key: crypto.randomUUID(),
		address: "",
		city: "",
		state: "",
		zip: "",
		phone: "",
		fax: "",
	};
}

type OrganizationFormProps = {
	/** Undefined to create a new organization. */
	organization: DirectoryOrganization | undefined;
	onSaved: (organization: DirectoryOrganization) => void;
};

/**
 * Create an organization, or edit every field of an existing one, including
 * its locations. Mount with `key` set to the organization's id so switching
 * organizations starts a fresh draft.
 */
export function OrganizationForm({
	organization,
	onSaved,
}: OrganizationFormProps) {
	const apiClient = useApiClient();
	const [companyName, setCompanyName] = useState(
		organization?.companyName ?? "",
	);
	const [aliases, setAliases] = useState(
		organization?.aliases.join(", ") ?? "",
	);
	const [npi, setNpi] = useState(organization?.npi ?? "");
	const [type, setType] = useState(organization?.type ?? "");
	// A new org starts with one blank location. An existing org starts with what
	// it has, even none, so renaming it doesn't demand an address.
	const [locations, setLocations] = useState<LocationDraft[]>(() =>
		organization ? organization.locations.map(toDraft) : [emptyLocation()],
	);
	const [isSaving, setIsSaving] = useState(false);

	const isReadOnly = organization !== undefined && !organization.editable;
	const canSave =
		!isReadOnly &&
		!isSaving &&
		companyName.trim() !== "" &&
		locations.every((location) => location.address.trim() !== "");

	const updateLocation = (key: string, changes: Partial<LocationDraft>) => {
		setLocations((current) =>
			current.map((location) =>
				location.key === key ? { ...location, ...changes } : location,
			),
		);
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const result = await apiClient.directoryAdmin.saveOrganization({
				id: organization?.id,
				companyName,
				aliases: aliases.split(","),
				npi,
				type,
				locations: locations.map(({ key: _key, ...location }) => location),
			});
			toast.success(organization ? "Organization saved" : "Organization added");
			onSaved(result.organization);
		} catch (error) {
			console.error("Failed to save organization:", error);
			toast.error(
				userFacingMessage(
					error,
					"We couldn't save this organization. Please try again",
				),
			);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Flex column gap="lg">
			{isReadOnly ? (
				<Flex
					gap="sm"
					align="center"
					padding="md"
					background="amber3"
					borderRadius="md"
				>
					<Icon name="TriangleAlert" size="sm" color="amber11" />
					<Text size="sm" color="amber11">
						Another practice's providers are linked to this organization, so it
						can't be edited from here.
					</Text>
				</Flex>
			) : null}

			<Flex column gap="md">
				<TextField
					label="Name"
					value={companyName}
					onChange={(e) => setCompanyName(e.target.value)}
					isDisabled={isReadOnly}
				/>
				<TextField
					label="Other names (comma-separated)"
					placeholder="e.g. Agave Therapy"
					value={aliases}
					onChange={(e) => setAliases(e.target.value)}
					isDisabled={isReadOnly}
				/>
				<Flex gap="md">
					<Flex column flex>
						<TextField
							label="Organization NPI (optional)"
							value={npi}
							onChange={(e) => setNpi(e.target.value)}
							isDisabled={isReadOnly}
						/>
					</Flex>
					<Flex column flex>
						<TextField
							label="Type (optional)"
							placeholder="e.g. home health agency"
							value={type}
							onChange={(e) => setType(e.target.value)}
							isDisabled={isReadOnly}
						/>
					</Flex>
				</Flex>
			</Flex>

			<Flex column gap="md">
				<Text size="md" weight="semibold" color="sand12">
					Locations
				</Text>
				{locations.map((location, index) => (
					<Flex
						key={location.key}
						column
						gap="sm"
						padding="md"
						background="sand2"
						borderRadius="md"
					>
						<Flex justify="space-between" align="center">
							<Text size="sm" weight="medium" color="sand11">
								{location.id ? `Location ${index + 1}` : "New location"}
							</Text>
							{location.id ? null : (
								<Button
									variant="ghost"
									size="xs"
									aria-label="Remove new location"
									onPress={() =>
										setLocations((current) =>
											current.filter((other) => other.key !== location.key),
										)
									}
								>
									<Icon name="Trash" size="xs" color="red10" />
								</Button>
							)}
						</Flex>
						<TextField
							label="Street address"
							value={location.address}
							onChange={(e) =>
								updateLocation(location.key, { address: e.target.value })
							}
							isDisabled={isReadOnly}
						/>
						<Flex gap="sm" align="flex-end">
							<Flex column flex>
								<TextField
									label="City"
									value={location.city}
									onChange={(e) =>
										updateLocation(location.key, { city: e.target.value })
									}
									isDisabled={isReadOnly}
								/>
							</Flex>
							<Select
								label="State"
								placeholder="State"
								options={US_STATES}
								value={location.state || undefined}
								onSelectionChange={(value) =>
									updateLocation(location.key, { state: value ?? "" })
								}
								disabled={isReadOnly}
								size="sm"
							/>
							<Flex column style={{ width: 100 }}>
								<TextField
									label="ZIP"
									value={location.zip}
									onChange={(e) =>
										updateLocation(location.key, { zip: e.target.value })
									}
									isDisabled={isReadOnly}
								/>
							</Flex>
						</Flex>
						<Flex gap="sm">
							<Flex column flex>
								<TextField
									label="Phone"
									value={location.phone}
									onChange={(e) =>
										updateLocation(location.key, { phone: e.target.value })
									}
									isDisabled={isReadOnly}
								/>
							</Flex>
							<Flex column flex>
								<TextField
									label="Fax"
									value={location.fax}
									onChange={(e) =>
										updateLocation(location.key, { fax: e.target.value })
									}
									isDisabled={isReadOnly}
								/>
							</Flex>
						</Flex>
					</Flex>
				))}
				{isReadOnly ? null : (
					<Button
						variant="outline"
						size="sm"
						alignSelf="start"
						onPress={() =>
							setLocations((current) => [...current, emptyLocation()])
						}
					>
						<Flex gap="xs" align="center">
							<Icon name="Plus" size="sm" />
							<Text size="sm">Add location</Text>
						</Flex>
					</Button>
				)}
			</Flex>

			{isReadOnly ? null : (
				<Button alignSelf="start" isDisabled={!canSave} onPress={handleSave}>
					{isSaving
						? "Saving..."
						: organization
							? "Save organization"
							: "Add organization"}
				</Button>
			)}
		</Flex>
	);
}
